import type {
  Country,
  DateTime,
  Grade,
  LinkInternal,
  MonetaryAmount,
  Url,
} from '@backmarket/http-api'

export const ORDERLINE_STATES = {
  NEW: 'NEW',
  PAID: 'PAID',
  VALIDATED: 'VALIDATED',
  SHIPPED: 'SHIPPED',
  CANCELLED: 'CANCELLED',
  REFUNDED: 'REFUNDED',
  NOT_PAID: 'NOT_PAID',
  REPLACED: 'REPLACED',
  REFUNDED_PRE_DELIVERY: 'REFUNDED_PRE_DELIVERY',
  REFUNDED_POST_DELIVERY: 'REFUNDED_POST_DELIVERY',
  PENDING: 'PENDING',
  ON_HOLD: 'ON_HOLD',
} as const

export type OrderlineStates =
  (typeof ORDERLINE_STATES)[keyof typeof ORDERLINE_STATES]

export const productRepairStatuses = [
  'CREATED',
  'RECEIVED',
  'DEVICE_LOCKED',
  'DEVICE_UNLOCKED',
  'OUT_OF_WARRANTY',
  'REPAIRED',
  'NOT_REPAIRED',
  'SHIPPED_TO_CUSTOMER',
] as const
export type ProductRepairStatuses = typeof productRepairStatuses
export type ProductRepairStatus = ProductRepairStatuses[number]

export type Attachment = {
  attachmentId: number
  name?: string | null
  url?: Url | null
  creationDate: DateTime
  handle?: string | null
  sharedWithSeller: boolean
}

export type CareFolder = {
  publicId: string
  customerRequestId: number
  orderline: Orderline
  merchant: Seller
  customer: Customer
  product: Product
  productReturnState: ProductReturnState
  lastCustomerToSellerTrackingNumber: string | null
  backcare: boolean
  productReturn: ProductReturn | null
  events: Event[]
  hasActiveInsurancePolicy: boolean
  remainingHours: number
  customerMessagesVisibleToSeller: boolean
  lastAttachmentSharedAt: DateTime | null
  repairStatus: ProductRepairStatus | null
  attachments: Attachment[]
  readOnly: boolean
}

export const productReturnStates = [
  'NO_PRODUCT_RETURN',
  'PRODUCT_RETURN_CREATED',
  'PRODUCT_RETURN_PICKED_UP',
  'PRODUCT_RETURN_DELIVERED',
  'PRODUCT_RETURN_EXPIRED',
  'RETURN_OF_PRODUCT_RETURN_CREATED',
  'RETURN_OF_PRODUCT_RETURN_PICKED_UP',
  'RETURN_OF_PRODUCT_RETURN_DELIVERED',
] as const
export type ProductReturnStates = typeof productReturnStates
export type ProductReturnState = ProductReturnStates[number]

export type Orderline = {
  orderlineId: number
  publicId: string
  orderId: number
  order: Order
  state: OrderlineStates
  creationDate: DateTime
  validationDate: DateTime
  shippingDate: DateTime | null
  refundDate: DateTime
  price: string
  shippingPrice: string
  backlabelPrice: string
  salesTaxPrice: string
  partnership: boolean
  quantity: number
  deliveryFormUrl: Url
  itemIdentifier: { type: 'IMEI' | 'SERIAL_NUMBER'; value: string }
  shippingAddress: Address
  billingAddress: Address
  delivery: Delivery
  coverage: Coverage | null
  customerShippingEmail: string
  customerInvoiceEmail: string | null
  refundLists: {
    successfulRefunds: Refund[]
    pendingRefunds: Refund[]
  }
  replacementOfInitialOrderline: null | {
    id: number
    publicId: string
    orderId: number
  }
  expressReplacedByReplacementOrderline: null | {
    id: number
    publicId: string
    orderId: number
    creationDate: DateTime
  }
  expressReplacementOfInitialOrderline: null | {
    id: number
    publicId: string
    orderId: number
  }
}

export type EventName =
  | 'CREATED'
  | 'RECEIVED'
  | 'DEVICE_LOCKED'
  | 'DEVICE_UNLOCKED'
  | 'OUT_OF_WARRANTY'
  | 'REPAIRED'
  | 'NOT_REPAIRED'
  | 'SHIPPED_TO_CUSTOMER'

export type Event = {
  source: string
  type: 'REPAIR' | 'TRACKING' | 'PROBLEM'
  name: EventName
  details: object
  occurredAt: DateTime
  id: string
}

export type ProductReturn = {
  creationDate: DateTime
  productReturnId: number
  kind:
    | 'UNSET'
    | 'CUSTOMER_TO_MERCHANT'
    | 'MERCHANT_TO_CUSTOMER'
    | 'CUSTOMER_TO_FACTORY'
  shipmentId: number
  claimId: number
}

export type Product = {
  productId: string
  name: string
  title: string
  grade: string
  imageUrl: Url
  comment: string
  sku: string
  backboxGrade: Grade
  isYakuzaPhone: boolean
  isNewBatteryDevice: boolean
  productSlug: string
  akeneoId: string
}

export type Customer = {
  customerId: number
  firstName: string
  lastName: string
}

export type Seller = {
  merchantId: number
  name: string
  defaultLocale: string
  backship: boolean
  exitProcess: boolean
}

export type Delivery = {
  minDeliveryDate: DateTime | null
  maxDeliveryDate: DateTime | null
  deliveryDate: DateTime | null
  delivered: boolean
  trackingUrl: Url | null
  trackingNumber: string | null
  carrierName: string | null
  isBackship: boolean
}

export type Warranty = {
  type: 'SELLER' | 'SELLER_EXTENSION' | 'BACKMARKET' | 'TRUSTPACK'
  duration: number
  startDate: DateTime
  endDate: DateTime
}

export type Insurance = {
  id: number
  type: number
  title: string
  duration: number
  startDate: DateTime
  endDate: DateTime
  maxEndDate: DateTime
  renewableYears: number
  state: number
  terminationDate: DateTime
  cancellationDate: DateTime
  failedPaymentsCount: number
  withdrawable: boolean
}

export type Coverage = {
  merchantWarranty: {
    duration: number
    endDate: DateTime
  }
  merchantWarrantyExtension:
    | {
        duration: number
        startDate: DateTime
        endDate: DateTime
        price?: MonetaryAmount
      }
    | Record<string, never>
  backmarketWarranty:
    | {
        duration: number
        startDate: DateTime
        endDate: DateTime
      }
    | Record<string, never>
  trustpackPlus:
    | {
        duration: number
        startDate: DateTime
        endDate: DateTime
        price?: MonetaryAmount
        termsAndConditionsUrl: Url
      }
    | Record<string, never>
  trustpackInsurance:
    | {
        id: number
        duration: number
        startDate: DateTime
        endDate: DateTime
        cancellationDate: DateTime | null
        terminationDate: DateTime | null
        price?: MonetaryAmount
        termsAndConditionsUrl: Url
        paymentFrequency: string
        state: 'NEW' | 'IN_PROGRESS' | 'OVER' | 'CANCELED' | 'PENDING'
        nbFailedPayments: number
      }
    | Record<string, never>
  chillpackInsurance:
    | {
        id: number
        duration: number
        startDate: DateTime
        endDate: DateTime
        cancellationDate: DateTime | null
        terminationDate: DateTime | null
        maxEndDate: DateTime | null
        price?: MonetaryAmount
        termsAndConditionsUrl: Url
        paymentFrequency: string
        state: 'NEW' | 'IN_PROGRESS' | 'OVER' | 'CANCELED' | 'PENDING'
        nbFailedPayments: number
        isRenewable: boolean
      }
    | Record<string, never>
  warranties: Warranty[]
  insurances: Insurance[]
  insurancePolicies: InsurancePolicy[]
}

export type Refund = {
  id: string
  creationDate: DateTime
  monetaryAmount: MonetaryAmount
  initiatorType: string
  initiatorEmail: string | ''
}

export type PolicyCoverage = {
  policyId: number
  kind: 'WARRANTY_EXTENSION' | 'DAMAGE' | 'THEFT'
  startDate: DateTime | null
  endDate: DateTime | null
  isActive: boolean
}

export type InsurancePolicy = {
  id: string | number
  publicId: string
  partnerName: string
  partnerPortalURL: Url
  offerTitle: string
  orderlineId: number
  state: 'CREATED' | 'SUBSCRIBED' | 'EXPIRED' | 'TERMINATED' | 'ON_HOLD'
  creationDate: DateTime
  subscriptionDate: DateTime | null
  expirationDate: DateTime | null
  terminationDate: DateTime | null
  compliancyDocuments: {
    name: string
    url: Url
    kind: 'TERMS_AND_CONDITIONS' | 'IPID' | 'BACKMARKET_RECAP'
  }[]
  price?: MonetaryAmount
  coverages: PolicyCoverage[]
  isRenewable: boolean
  isRenewed: boolean
  isWithdrawable: boolean
  withdrawalPeriodEndDate: DateTime
  hasPendingTerminationRequest: boolean
  isMonthly: boolean
  failingOccurrencesLink: LinkInternal | null
  maxDuePaymentDate: DateTime | null
}

export type Address = {
  addressId: number
  countryDialInCode: string
  city: string
  phone: string
  firstName: string
  lastName: string
  street: string
  street2: string | null
  postalCode: string | null
  country: Country
  companyName: string | null
  stateOrProvince: string | null
  firstNamePronunciation: string | null
  lastNamePronunciation: string | null
}

export type Order = {
  orderId: number
  creationDate: DateTime
  countryCode: string
  locale: string
}
